"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FirebaseCore = void 0;
const app_1 = require("firebase/app");
const PlatformDetector_1 = require("../platforms/PlatformDetector");
const WebPlatformAdapter_1 = require("../platforms/WebPlatformAdapter");
const MobilePlatformAdapter_1 = require("../platforms/MobilePlatformAdapter");
const AuthManager_1 = require("../services/AuthManager");
const FirestoreManager_1 = require("../services/FirestoreManager");
const StorageManager_1 = require("../services/StorageManager");
const AnalyticsManager_1 = require("../services/AnalyticsManager");
const FunctionsManager_1 = require("../services/FunctionsManager");
const AppCheckManager_1 = require("../services/AppCheckManager");
const EmulatorConnector_1 = require("../emulators/EmulatorConnector");
const types_1 = require("../types");
/**
 * Firebase Core Coordinator
 * Lightweight orchestrator that manages all Firebase service modules
 */
class FirebaseCore {
    constructor() {
        this.initialized = false;
        this.app = null;
        // Service managers
        this.authManager = new AuthManager_1.AuthManager();
        this.firestoreManager = new FirestoreManager_1.FirestoreManager();
        this.storageManager = new StorageManager_1.StorageManager();
        this.analyticsManager = new AnalyticsManager_1.AnalyticsManager();
        this.functionsManager = new FunctionsManager_1.FunctionsManager();
        this.appCheckManager = new AppCheckManager_1.AppCheckManager();
        this.emulatorConnector = new EmulatorConnector_1.EmulatorConnector();
        // Service instances (populated after initialization)
        this.services = {
            app: null,
            firestore: null,
            auth: null,
            storage: null,
            analytics: null,
            functions: null,
        };
    }
    static getInstance() {
        if (!this.instance) {
            this.instance = new FirebaseCore();
        }
        return this.instance;
    }
    /**
     * Initialize Firebase with the appropriate platform configuration
     */
    initialize() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.initialized) {
                return;
            }
            console.log('🔧 FirebaseCore: Starting initialization...');
            try {
                // Detect platform and get appropriate adapter
                const platform = PlatformDetector_1.PlatformDetector.detect();
                console.log(`🌍 FirebaseCore: Detected platform: ${platform}`);
                const adapter = this.getPlatformAdapter(platform);
                const platformConfig = adapter.getConfig();
                // Initialize Firebase app if not already done
                if ((0, app_1.getApps)().length === 0) {
                    console.log('🚀 FirebaseCore: Initializing Firebase app...');
                    this.app = (0, app_1.initializeApp)(platformConfig.config);
                }
                else {
                    console.log('♻️ FirebaseCore: Using existing Firebase app...');
                    this.app = (0, app_1.getApp)();
                }
                // Update services object
                this.services.app = this.app;
                // Initialize App Check BEFORE other services
                // This ensures all Firebase services are protected
                yield this.appCheckManager.initialize(this.app);
                // Initialize all service managers
                yield this.initializeServices();
                // Setup emulators if configured
                if (platformConfig.useEmulators && platformConfig.emulatorPorts) {
                    const nonNullServices = {
                        firestore: this.services.firestore,
                        auth: this.services.auth,
                        functions: this.services.functions
                    };
                    yield this.emulatorConnector.connectAll(nonNullServices, platformConfig.emulatorPorts);
                }
                this.initialized = true;
                console.log('✅ FirebaseCore: Initialization complete');
            }
            catch (error) {
                console.error('❌ FirebaseCore: Initialization failed:', error);
                throw error;
            }
        });
    }
    /**
     * Initialize all Firebase service managers
     */
    initializeServices() {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🚨 DEBUG: initializeServices() CALLED');
            console.log('🚨 DEBUG: this.app exists?', !!this.app);
            if (!this.app) {
                console.error('🚨 DEBUG: Firebase app is null!');
                throw new Error('Firebase app must be initialized first');
            }
            console.log('🔧 FirebaseCore: Initializing service managers...');
            try {
                console.log('🔧 DEBUG: About to initialize Firestore...');
                this.services.firestore = yield this.firestoreManager.initialize(this.app);
                console.log('✅ DEBUG: Firestore initialized');
            }
            catch (error) {
                console.error('❌ DEBUG: Firestore initialization FAILED:', error);
                throw error;
            }
            this.services.auth = yield this.authManager.initialize(this.app);
            this.services.storage = yield this.storageManager.initialize(this.app);
            this.services.functions = yield this.functionsManager.initialize(this.app);
            // Analytics is optional and may not be available on all platforms
            try {
                this.services.analytics = yield this.analyticsManager.initialize(this.app);
            }
            catch (error) {
                console.log('ℹ️ FirebaseCore: Analytics not available on this platform');
                this.services.analytics = null;
            }
            console.log('✅ FirebaseCore: All service managers initialized');
        });
    }
    /**
     * Get platform-specific adapter
     */
    getPlatformAdapter(platform) {
        switch (platform) {
            case types_1.Platform.Web:
                return new WebPlatformAdapter_1.WebPlatformAdapter();
            case types_1.Platform.ReactNative:
                return new MobilePlatformAdapter_1.MobilePlatformAdapter();
            default:
                throw new Error(`Unsupported platform: ${platform}`);
        }
    }
    // Getters for service managers
    getAuthManager() {
        return this.authManager;
    }
    getFirestoreManager() {
        return this.firestoreManager;
    }
    getStorageManager() {
        return this.storageManager;
    }
    getAnalyticsManager() {
        return this.analyticsManager;
    }
    getFunctionsManager() {
        return this.functionsManager;
    }
    getAppCheckManager() {
        return this.appCheckManager;
    }
    // Status getters
    isInitialized() {
        return this.initialized;
    }
    getApp() {
        return this.app;
    }
    getServices() {
        return this.services;
    }
}
exports.FirebaseCore = FirebaseCore;
FirebaseCore.instance = null;
